import React, { useState } from 'react';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import getSuitCSSClasses from '@/utils/getSuitCSSClasses';
import TreeItem from '@/client/components/FilterSidebar/atoms/TreeItem';
import {
  RowHeader,
  Accordion,
  AccordionBody,
  AccordionItem,
  AccordionHeader,
  StandAloneLink,
} from '@hz-design-system/web-ui';
import trackingKeys from '@/client/constants/analytics/trackingKeys';
import LinkGenerator from '@/client/components/LinkGenerator/LinkGenerator';

export interface CategoryTreeFacetProps {
  categories: TFacetCategory[];
  inputKey: string;
  label: string;
  isMobile: boolean;
  searchRequestObject: TSearchRequestObject;
  maxL2Categories?: number;
  maxL1Categories?: number;
  isCarsCategory?: boolean;
}

type FormattedCategory = TFacetCategory & {
  items: TFacetCategory[];
  selected: boolean;
};

const DEFAULT_MAX_L2_CATEGORIES = 4;
const DEFAULT_MAX_L1_CATEGORIES = 3;

const getFormattedCategories = (categories: TFacetCategory[]): FormattedCategory[] => {
  return Array.isArray(categories)
    ? categories.reduce<FormattedCategory[]>((acc, item) => {
        if (item.parentId) {
          const parentCategory = acc.find((cat) => cat.id === item.parentId);
          if (parentCategory) {
            parentCategory.items.push(item);
            parentCategory.histogramCount = (parentCategory.histogramCount || 0) + (item.histogramCount || 0);
          }
        } else {
          acc.push({
            ...item,
            items: [],
            selected: item.selected,
            histogramCount: 0,
          });
        }
        return acc;
      }, [])
    : [];
};

const CategoryTreeFacet: React.FC<CategoryTreeFacetProps> = ({
  categories,
  inputKey,
  label,
  searchRequestObject,
  maxL2Categories = DEFAULT_MAX_L2_CATEGORIES,
  maxL1Categories = DEFAULT_MAX_L1_CATEGORIES,
  isCarsCategory = false,
}) => {
  const [isExpanded, setIsExpanded] = useState<number[]>([]);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const { t } = useI18nContext();
  const formattedCategories = getFormattedCategories(categories);
  const visibleCategories = showAllCategories ? formattedCategories : formattedCategories.slice(0, maxL1Categories);
  const hideIndex = maxL2Categories;
  const toggleExpanded = (l1CategoryId: number) => {
    if (isExpanded.includes(l1CategoryId)) {
      setIsExpanded((prevState) => prevState.filter((id) => id !== l1CategoryId));
    } else {
      setIsExpanded((prevState) => [...prevState, l1CategoryId]);
    }
  };

  const toggleShowAllCategories = () => {
    setShowAllCategories((prev) => !prev);
  };

  return (
    <div className={getSuitCSSClasses({ namespace: 'hz', name: 'Filter' })}>
      <div className="category-row-header">
        <RowHeader title={t(`facets.${inputKey}`, label)} />
        {!isCarsCategory && formattedCategories.length === 1 && (
          <LinkGenerator
            searchRequestObject={searchRequestObject}
            removeAllFilters={true}
            removeAllAttributes={true}
            className="category-clear"
            eventAction={trackingKeys.FILTER.EVENT_ACTION}
            eventLabel={trackingKeys.FILTER.EVENT_LABEL.PARENT_CATEGORY}
          >
            {t('facets.category.clear')}
          </LinkGenerator>
        )}
      </div>
      {Array.isArray(visibleCategories)
        ? visibleCategories.map(
            (
              {
                label: l1CategoryLabel,
                items,
                id: l1CategoryId,
                key: l1CategoryKey,
                selected: l1Selected,
                histogramCount: l1histogramCount,
              },
              index,
            ) => {
              return (
                <div key={l1CategoryKey} id={l1CategoryId.toString()}>
                  <Accordion>
                    <AccordionItem open={index === 0 ? true : undefined}>
                      <AccordionHeader>
                        <span className="category-header">{l1CategoryLabel}</span>
                      </AccordionHeader>
                      <AccordionBody className="category-body">
                        <>
                          <TreeItem.LevelOne
                            categories={{
                              l1Category: {
                                key: l1CategoryKey,
                                id: l1CategoryId,
                              },
                            }}
                            text={l1CategoryKey}
                            searchRequestObject={searchRequestObject}
                            selected={l1Selected}
                            count={l1histogramCount}
                          />
                          {items.map(
                            (
                              { label: itemLabel, histogramCount, id: l2CategoryId, key: l2CategoryKey, selected },
                              idx,
                            ) => {
                              return (
                                <TreeItem.LevelTwo
                                  categories={{
                                    l1Category: {
                                      key: l1CategoryKey,
                                      id: l1CategoryId,
                                    },
                                    l2Category: {
                                      key: l2CategoryKey,
                                      id: l2CategoryId,
                                    },
                                  }}
                                  key={l2CategoryKey}
                                  text={itemLabel}
                                  count={histogramCount}
                                  hidden={idx >= hideIndex && !isExpanded.includes(l1CategoryId)}
                                  searchRequestObject={searchRequestObject}
                                  selected={selected}
                                />
                              );
                            },
                          )}
                          {items.length > maxL2Categories && (
                            <StandAloneLink
                              iconRight={isExpanded.includes(l1CategoryId) ? 'arrow-up-regular' : 'arrow-down-regular'}
                              onClick={() => toggleExpanded(l1CategoryId)}
                              onKeyDown={(e) => e.key === 'Enter' && toggleExpanded(l1CategoryId)}
                              className={getSuitCSSClasses({ namespace: 'hz', name: 'Level-two', modifiers: ['more'] })}
                              aria-label={`${isExpanded.includes(l1CategoryId) ? t('facets.less') : t('facets.more')} ${l1CategoryLabel}`}
                              aria-live="polite"
                            >
                              {isExpanded.includes(l1CategoryId) ? t('facets.less') : t('facets.more')}
                            </StandAloneLink>
                          )}
                        </>
                      </AccordionBody>
                    </AccordionItem>
                  </Accordion>
                </div>
              );
            },
          )
        : null}
      {formattedCategories.length > maxL1Categories && (
        <StandAloneLink
          iconRight={showAllCategories ? 'arrow-up-regular' : 'arrow-down-regular'}
          onClick={toggleShowAllCategories}
          className={getSuitCSSClasses({ namespace: 'hz', name: 'Level-one', modifiers: ['more'] })}
          aria-label={showAllCategories ? t('facets.less') : t('facets.more')}
          aria-live="polite"
        >
          {showAllCategories ? t('facets.less') : t('facets.more')}
        </StandAloneLink>
      )}
    </div>
  );
};

export default CategoryTreeFacet;
